import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { registerTeamForTournament, fetchTournamentDetails } from '../../api/TournamentsApi';
import { fetchPlayerTeams } from '../../api/PlayerDataApi';

const TeamTournamentRegistrationModal = ({ isOpen, onClose, tournamentId, game, isRegistered }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missingGameIds, setMissingGameIds] = useState([]);
  const [memberGameIds, setMemberGameIds] = useState({});
  const [memberGameUsernames, setMemberGameUsernames] = useState({});
  const [success, setSuccess] = useState(null);
  
  // Helper function to get current user ID
  const getUserId = () => {
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    return userInfo.userId || userInfo.id || userInfo.user_id;
  };
  
  // Determine if the user is a leader of any team
  const hasLeaderTeams = teams.some(team => {
    // Check various properties to determine leadership role
    return team.role === "Leader" || 
           team.role === "Owner" || 
           (team.team_owner && team.team_owner.id === getUserId()) ||
           team.team_owner_id === getUserId() ||
           team.owner_id === getUserId();
  });

  // Load user's teams that match the game type
  useEffect(() => {
    if (isOpen && !isRegistered) {
      const loadTeams = async () => {
        try {
          setTeamsLoading(true);
          const response = await fetchPlayerTeams();
          
          // Check if teams exist in the response
          if (!response.teams || !Array.isArray(response.teams)) {
            console.error("Invalid teams response format:", response);
            setError("Could not load teams. Invalid data format.");
            setTeams([]);
            return;
          }
          
          // Map game names to types with more flexibility
          const gameTypeMap = {
            'free fire': 'freefire',
            'freefire': 'freefire',
            'pubg': 'pubg',
            'pubg mobile': 'pubg',
            'bgmi': 'bgmi',
            'call of duty': 'cod',
            'cod': 'cod',
            'valorant': 'valorant'
          };
          
          // Normalize the current game name for comparison
          const gameToCheck = game ? game.toLowerCase().trim() : '';
          const normalizedGameType = gameTypeMap[gameToCheck] || gameToCheck;
          
          // First attempt - try matching with exact game type
          let filteredTeams = response.teams.filter(team => {
            if (team.team_type) {
              return team.team_type.toLowerCase() === normalizedGameType;
            }
            return false;
          });
          
          // If no teams found, try a more lenient approach
          if (filteredTeams.length === 0) {
            filteredTeams = response.teams;
          }
          
          setTeams(filteredTeams);
          setError(null);
        } catch (err) {
          console.error("Error loading teams:", err);
          setError("Failed to load your teams. Please try again.");
        } finally {
          setTeamsLoading(false);
        }
      };
      
      loadTeams();
    }
  }, [isOpen, isRegistered, game]);

  // Handle member game ID/username changes
  const handleGameIdChange = (playerId, value) => {
    setMemberGameIds(prev => ({
      ...prev,
      [playerId]: value
    }));
  };

  const handleGameUsernameChange = (playerId, value) => {
    setMemberGameUsernames(prev => ({
      ...prev,
      [playerId]: value
    }));
  };

  // Handle team registration
  const handleRegister = async () => {
    if (!selectedTeamId) {
      setError("Please select a team to register");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Log payload for debugging
      let payload = { team_id: selectedTeamId };
      console.log("Registering team with payload:", payload);
      
      // If we have missing game IDs, include them in the payload
      if (missingGameIds.length > 0) {
        payload.member_game_ids = memberGameIds;
        payload.member_game_usernames = memberGameUsernames;
      }
      
      const result = await registerTeamForTournament(tournamentId, payload);
      console.log("Registration success:", result);
      
      setSuccess({
        message: result.message || "Successfully registered team for the tournament!",
        teamName: result.team_name
      });
      
      // Clear form fields
      setMissingGameIds([]);
      setMemberGameIds({});
      setMemberGameUsernames({});
      
      // Close the modal after a delay
      setTimeout(() => {
        onClose();
        // Refresh the tournaments list to show updated registration status
        window.location.reload();
      }, 2000);
      
    } catch (err) {
      console.error("Error registering team:", err);
      
      // Handle missing game IDs error
      if (err.missingPlayerIds) {
        setMissingGameIds(err.missingPlayerIds);
        setError("Game IDs are required for all team members. Please provide the missing information.");
        
        // Initialize empty fields for missing players
        const initialGameIds = {};
        const initialGameUsernames = {};
        err.missingPlayerIds.forEach(playerId => {
          initialGameIds[playerId] = '';
          initialGameUsernames[playerId] = '';
        });
        
        setMemberGameIds(prev => ({...prev, ...initialGameIds}));
        setMemberGameUsernames(prev => ({...prev, ...initialGameUsernames}));
      } else {
        setError(err.message || "Failed to register team for the tournament. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Find player names for the missing IDs
  const getPlayerName = (playerId) => {
    // Try to find the selected team
    const selectedTeam = teams.find(team => team.id === selectedTeamId);
    if (!selectedTeam || !selectedTeam.members_details) return "Unknown Player";
    
    // Find the player in the team members
    const player = selectedTeam.members_details.find(member => member.user_id === playerId);
    return player ? (player.username || player.email) : "Unknown Player";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-8">
        <div className="px-6 py-4 bg-gradient-to-r from-primary-700 to-primary-800 text-white rounded-t-lg">
          <h2 className="text-xl font-bold">Register Team for Tournament</h2>
          <p className="text-sm text-primary-100">Select your team to participate</p>
        </div>
        
        {/* Modal body with max height and scroll */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {isRegistered ? (
            <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>You're already registered for this tournament.</span>
              </div>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Registration Successful!</h3>
              <p className="text-gray-600">{success.message}</p>
              {success.teamName && (
                <p className="text-gray-500 mt-1">Team: {success.teamName}</p>
              )}
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
                  <p>{error}</p>
                </div>
              )}
              
              {teamsLoading ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-3 text-gray-500">Loading your teams...</p>
                </div>
              ) : teams.length === 0 ? (
                <div className="py-8 text-center">
                  <svg className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No Teams Available</h3>
                  <p className="text-gray-500 mb-4">You don't have any teams for this game type.</p>
                  <Link 
                    to="/player/teams/create" 
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    onClick={onClose}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create a Team
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Team Selection */}
                  <div>
                    <label htmlFor="team-select" className="block text-sm font-medium text-gray-700 mb-1">
                      Select Your Team
                    </label>
                    <select
                      id="team-select"
                      value={selectedTeamId || ""}
                      onChange={(e) => {
                        setSelectedTeamId(e.target.value);
                        // Reset missing IDs when team changes
                        setMissingGameIds([]);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="" disabled>Choose a team</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({team.members_details?.length || 0} members)
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Missing Game IDs Form */}
                  {missingGameIds.length > 0 && (
                    <div className="mt-4 border border-yellow-200 rounded-md bg-yellow-50 p-4">
                      <h3 className="text-sm font-medium text-yellow-800 mb-3">
                        Missing Game Information
                      </h3>
                      <p className="text-xs text-yellow-700 mb-3">
                        Please provide game IDs and usernames for the following team members:
                      </p>
                      
                      <div className="space-y-3 max-h-[40vh] overflow-y-auto pb-2">
                        {missingGameIds.map(playerId => (
                          <div key={playerId} className="p-3 bg-white rounded border border-yellow-100">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                              {getPlayerName(playerId)}
                            </p>
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Game ID</label>
                                <input
                                  type="text"
                                  value={memberGameIds[playerId] || ''}
                                  onChange={(e) => handleGameIdChange(playerId, e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                  placeholder="Enter game ID"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Game Username</label>
                                <input
                                  type="text"
                                  value={memberGameUsernames[playerId] || ''}
                                  onChange={(e) => handleGameUsernameChange(playerId, e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                  placeholder="Enter username"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer with fixed position */}
        <div className="bg-gray-50 px-6 py-4 border-t sticky bottom-0 rounded-b-lg">
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {!isRegistered && !success && (
              <button
                type="button"
                onClick={handleRegister}
                disabled={teamsLoading || loading || teams.length === 0 || !selectedTeamId}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Registering...
                  </>
                ) : (
                  'Register Team'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamTournamentRegistrationModal;